import 'swiper/css'
import 'swiper/css/scrollbar'
import 'swiper/css/free-mode'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'styled-components'
import type SwiperType from 'swiper'
import { Scrollbar, FreeMode, Mousewheel } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { Section } from 'components/section'
import { timelineData } from 'data/timeline'
import { useMenuItem } from 'hooks/useMenuItem'

import { TimelineDescription } from './parts/timeline-description'
import {
  Entries,
  SectionContainer,
  SliderContainer,
  TimelineContainer,
  Year,
  YearLabel,
  Years,
} from './styled'
import { TimelineEntry } from './timeline-entry'

export const Timeline = () => {
  const ref = useMenuItem()
  const container = useRef<HTMLDivElement>(null)
  const swiper = useRef<SwiperType>(null)
  const [items, setItems] =
    useState<
      Array<(typeof timelineData)[number] & { element?: Element | null }>
    >(timelineData)
  const [observer, setObserver] = useState<IntersectionObserver>()
  const [entryIndex, setEntryIndex] = useState<{
    current: number
    previous: number
  }>({
    current: 0,
    previous: 0,
  })
  const [groupedData, activeEntry, previousEntry, nextEntry] = useMemo(() => {
    const modifiedEntries = items.map((item, index) => ({
      ...item,
      index,
      isActive: index === entryIndex.current,
    }))

    const index = modifiedEntries.findIndex(entry => entry.isActive)

    return [
      Object.groupBy(modifiedEntries, ({ year }) => year),
      modifiedEntries[index],
      index > 0 ? modifiedEntries[index - 1] : null,
      modifiedEntries[index + 1] ?? null,
    ]
  }, [entryIndex, items])

  useEffect(() => {
    const o = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setEntryIndex(v => ({
              previous: v.current,
              current: Number(entry.target.getAttribute('data-index')),
            }))
          }
        })
      },
      { rootMargin: '0% -50% 0% -50%', root: container.current }
    )
    setObserver(o)

    return () => {
      observer?.disconnect()
    }
  }, [])

  const goToElement = (element?: Element | null) => {
    if (swiper.current && element) {
      const translate = swiper.current.translate
      const boundingBox = element.getBoundingClientRect()
      const delta = -(
        window.innerWidth / 2 -
        (boundingBox.left + boundingBox.width / 2)
      )

      swiper.current.translateTo(translate - delta, 300)
    }
  }

  return (
    <Section ref={ref} title="таймлайн" data-menu-label="таймлайн">
      <SectionContainer>
        <SliderContainer ref={container}>
          <Swiper
            onInit={s => {
              swiper.current = s
            }}
            slidesPerView="auto"
            modules={[Scrollbar, FreeMode, Mousewheel]}
            direction="horizontal"
            scrollbar={{ enabled: true, draggable: true }}
            mousewheel
            freeMode
          >
            <SwiperSlide style={{ width: 'max-content' }}>
              <TimelineContainer>
                <Years>
                  {Object.entries(groupedData).map(
                    ([year, entries], index, all) => {
                      const thisYear =
                        index > 0 ? Number(year) : Number(all[index + 1][0])
                      const lastYear =
                        index > 0
                          ? Number(all[index - 1][0])
                          : Number(all[index][0])

                      return (
                        <Year key={year}>
                          <YearLabel
                            data-ticks-count={thisYear - lastYear}
                            style={
                              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                              {
                                '--ticks-count': thisYear - lastYear,
                              } as CSSProperties
                            }
                          >
                            {year}
                          </YearLabel>
                          <Entries>
                            {entries?.map(entry => (
                              <TimelineEntry
                                onClick={goToElement}
                                onMount={element =>
                                  setItems(v =>
                                    v.map((i, n) =>
                                      entry.index === n ? { ...i, element } : i
                                    )
                                  )
                                }
                                observer={observer}
                                entry={entry}
                                key={entry.index}
                              />
                            ))}
                          </Entries>
                        </Year>
                      )
                    }
                  )}
                </Years>
              </TimelineContainer>
            </SwiperSlide>
          </Swiper>
        </SliderContainer>

        <TimelineDescription
          entryIndex={entryIndex}
          activeEntry={activeEntry}
          nextEntry={nextEntry}
          previousEntry={previousEntry}
          onChangeEntry={goToElement}
        />
      </SectionContainer>
    </Section>
  )
}
